import { Box, Flex, VStack, useDisclosure } from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';

import { Message } from '@/features/messages/messages.types';
import { useState, useRef, useEffect } from 'react';

import { ImagePreviewModal } from './conversation-image-preview-modal';
import { MessageSender } from './conversation-message-sender';
import { MessageAttachments } from './conversation-message-attachments';
import { MessageFeedback } from './conversation-message-feedback';
import { formatTime } from './conversation-utils';

interface MessageItemProps {
  message: Message;
  isUser: boolean;
  senderAvatar: string;
  senderName: string;
  isAIAgent?: boolean;
  messageStatus?: 'pending' | 'sending' | 'sent' | 'delivered' | 'error';
}

export function MessageItem({
  message,
  isUser,
  senderAvatar,
  senderName,
  isAIAgent = false,
  messageStatus = 'delivered',
}: MessageItemProps) {
  // State for the image modal
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Audio player state
  const [isPlaying, setIsPlaying] = useState<{ [key: string]: boolean }>({});
  const [progress, setProgress] = useState<{ [key: string]: number }>({});
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});

  // Update the formatDuration function for better second-level display
  const formatDuration = (seconds: number) => {
    if (seconds === undefined || seconds === null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60); // Ensure we're using an integer
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle image click to open the modal
  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    onOpen();
  };

  // Handle avatar click to open the modal
  const handleAvatarClick = () => {
    setSelectedImage(senderAvatar);
    onOpen();
  };

  // Download the image
  const handleDownloadImage = (imageUrl: string, imageName?: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageName || 'image';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle audio playback
  const toggleAudio = (audioId: string, audioUrl: string) => {
    let audio = audioRefs.current[audioId];

    // Initialize audio element if not already created
    if (!audio) {
      audio = new Audio(audioUrl);
      audioRefs.current[audioId] = audio;

      // Add event listeners
      audio.addEventListener('timeupdate', () => {
        if (audio && audio.duration) {
          // Ensure audio object still exists
          const percent = (audio.currentTime / audio.duration) * 100;
          // Use functional update to prevent stale state issues
          setProgress((prev) => ({ ...prev, [audioId]: percent }));
        }
      });

      audio.addEventListener('ended', () => {
        setIsPlaying((prev) => ({ ...prev, [audioId]: false }));
        setProgress((prev) => ({ ...prev, [audioId]: 0 }));
        if (audioRefs.current[audioId]) {
          audioRefs.current[audioId]!.currentTime = 0; // Reset time on end
        }
      });

      // Ensure initial progress state is set when the element is first created
      setProgress((prev) => ({ ...prev, [audioId]: 0 }));
    }

    // Now handle play/pause logic
    if (audio) {
      if (isPlaying[audioId]) {
        audio.pause();
        setIsPlaying((prev) => ({ ...prev, [audioId]: false }));
      } else {
        // Pause any other playing audio
        Object.keys(isPlaying).forEach((key) => {
          if (isPlaying[key] && audioRefs.current[key] && key !== audioId) {
            audioRefs.current[key]?.pause();
            setIsPlaying((prev) => ({ ...prev, [key]: false }));
          }
        });

        // Reset progress and current time before playing, especially if replaying
        audio.currentTime = 0;
        setProgress((prev) => ({ ...prev, [audioId]: 0 }));

        // Use a promise to ensure play starts, then update state
        audio
          .play()
          .then(() => {
            setIsPlaying((prev) => ({ ...prev, [audioId]: true }));
          })
          .catch((error) => {
            console.error('Error playing audio:', error);
            // Reset state if play fails
            setIsPlaying((prev) => ({ ...prev, [audioId]: false }));
            setProgress((prev) => ({ ...prev, [audioId]: 0 }));
          });
      }
    }
  };

  // Clean up audio elements on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      audioRefs.current = {};
    };
  }, []);

  // workaround to get the type of the message
  const isAudioTranscription = message.attachments?.[0]?.file_type === 'audio';

  return (
    <>
      <Flex
        justify={isUser ? 'flex-start' : 'flex-end'} // User messages left-aligned, agent messages right-aligned
        mb={6}
        position="relative"
      >
        {isUser && ( // User avatar on left
          <Avatar
            src={senderAvatar}
            name={senderName}
            mr={3}
            size="md"
            bg="gray.500"
            color="white"
            cursor="pointer"
            onClick={handleAvatarClick}
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.05)' }}
          />
        )}

        <VStack
          maxWidth="75%"
          align={isUser ? 'flex-start' : 'flex-end'}
          gap={0}
        >
          {/* Sender name and timestamp */}
          <MessageSender
            senderName={senderName}
            isUser={isUser}
            dateTime={formatTime(message.created_at)}
            isAIAgent={isAIAgent}
            message={message.content}
            hideMessage={isAudioTranscription}
            hasAttachments={
              !!(
                message?.attachments?.length && message?.attachments?.length > 0
              )
            }
          />

          {/* Message content */}
          <Box px={4} borderRadius="md" width="100%" position="relative">
            {/* For failed messages, add a subtle error indicator */}
            {messageStatus === 'error' && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                borderWidth="1px"
                borderColor="red.500"
                borderRadius="md"
                opacity={0.3}
                pointerEvents="none"
              />
            )}

            <MessageAttachments
              isUser={isUser}
              isAIAgent={isAIAgent}
              messageStatus={messageStatus}
              handleImageClick={handleImageClick}
              handleDownloadImage={handleDownloadImage}
              message={message}
              toggleAudio={toggleAudio}
              isPlaying={isPlaying}
              progress={progress}
              audioRefs={audioRefs}
              formatDuration={formatDuration}
            />

            <MessageFeedback message={message} />
          </Box>
        </VStack>

        {!isUser && ( // Agent avatar on right
          <Avatar
            src={senderAvatar}
            name={senderName}
            ml={3}
            size="md"
            bg={isAIAgent ? 'green.500' : 'blue.500'}
            color="white"
            cursor="pointer"
            onClick={handleAvatarClick}
            transition="transform 0.2s"
            _hover={{ transform: 'scale(1.05)' }}
          />
        )}
      </Flex>

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isOpen}
        onClose={onClose}
        imageUrl={selectedImage}
      />
    </>
  );
}
